export const cardplotFaqs = [
  {
    question: "How can I get started with Cardplot?",
    answer: "Getting started is simple and takes just a few steps. Click on “Get Started”, then choose whether you want to create a Personal or Business account. You can sign up quickly using your Google account, or simply register with your email address. Once you’ve signed up, enter the OTP sent to your email to verify your account and you’re all set to start using Cardplot."
  },
  {
    question: "How fast are transactions on Cardplot?",
    answer: "Most transactions on Cardplot are processed instantly or within a few minutes. However, processing time may vary slightly depending on network conditions or the type of transaction."
  },
  {
    question: "How do I get my referral code?",
    answer: "After successfully creating your account, a unique referral code will be automatically assigned to you. Once you log in, navigate to your profile section to access your referral code. You can then share it with others and earn rewards whenever someone signs up and uses Cardplot through your referral."
  },
  {
    question: "How do I create a wallet for another African country?",
    answer: "To create a wallet for another African country, simply sign in to your Cardplot account and navigate to your dashboard. Click on “Wallet”, then select “Create New Wallet” at the top right corner. Choose the country wallet you wish to create from the available options, then click “Confirm.” Your new wallet will be created instantly and ready for use."
  },
  {
    question: "How do I join the leaderboard contest?",
    answer: "To join the leaderboard contest, simply share your referral code and invite others to sign up on Cardplot. You can also complete simple tasks on the platform to earn points and boost your ranking. The more active you are, the higher you climb on the leaderboard and the more rewards you can earn."
  },
  {
    question: "What services can I use on Cardplot?",
    answer: "Cardplot allows you to perform a wide range of financial activities, including sending and receiving money across the African continent, paying bills, purchasing airtime and data, and managing cross-border transactions—all in one secure platform."
  },
  {
    question: "Can I send money across different African countries on Cardplot?",
    answer: "Yes, Cardplot is designed as a pan-African payment platform, allowing you to send and receive money across supported African countries quickly and seamlessly."
  },
  {
    question: "Do I need to verify my account?",
    answer: "Yes, for security and compliance purposes, users may be required to complete a verification process. This helps protect your account and ensures a safe transaction environment for all users."
  },
  {
    question: "How can I contact Cardplot customer support?",
    answer: "You can reach Cardplot support through the help or support section within the app or website. Our support team is available to assist you with any questions or issues you may have."
  },
  {
    question: "What should I do if my transaction fails?",
    answer: "If a transaction fails, kindly check your network connection and ensure all details entered are correct. If the issue persists, you can contact Cardplot support for quick assistance and resolution."
  }
];

export const faqData = [
  {
    id: 1,
    category: "sending_money",
    slug: "sending-money",
    title: "Sending Money",
    description: "Setting up, payments, editing and cancelling transfers",
    icon: "FaArrowUp",
    faqs: [
      {
        question: "How do I send money on Cardplot?",
        answer: "To send money, log in to your account and go to your dashboard. Select “Send Money”, choose the recipient (bank account or Cardplot user), enter the amount, and confirm the transaction."
      },
      {
        question: "How long does it take for money to be delivered?",
        answer: "Most transfers are processed instantly or within a few minutes. However, delivery time may vary depending on the recipient’s bank or network conditions."
      },
      {
        question: "Are there any fees for sending money?",
        answer: "Cardplot maintains transparent pricing. Any applicable fees will be clearly displayed before you confirm your transaction—no hidden charges."
      },
      {
        question: "Can I send money to someone who is not on Cardplot?",
        answer: "Yes, you can send money to non-Cardplot users by entering their bank account details. The funds will be delivered directly to their account."
      },
      {
        question: "What should I do if I send money to the wrong account?",
        answer: "If you make a mistake, contact Cardplot support immediately through the help section. While we’ll do our best to assist, successful recovery depends on the transaction status and recipient bank."
      }
    ]
  },
  {
    id: 2,
    category: "account_management",
    slug: "account-management",
    title: "Account Management",
    description: "Setting up your account and getting verified",
    icon: "MdManageAccounts",
    faqs: [
      {
        question: "How do I update my personal information on Cardplot?",
        answer: "You can update your personal details by navigating to your profile settings within the app or dashboard. Simply edit the required information and save your changes."
      },
      {
        question: "I forgot my password. How can I reset it?",
        answer: "Click on “Forgot Password” on the login page, enter your registered email address, and follow the instructions sent to your email to reset your password securely."
      },
      {
        question: "Can I switch between a Personal and Business account?",
        answer: "Yes, you can upgrade or switch your account type by accessing your account settings and selecting the appropriate option. Additional verification may be required for business accounts."
      },
      {
        question: "How do I delete or deactivate my Cardplot account?",
        answer: "To deactivate or delete your account, please contact Cardplot support through the help section. The team will guide you through the process and ensure your request is handled securely."
      },
      {
        question: "Why is my account restricted or limited?",
        answer: "Your account may be restricted due to security checks, incomplete verification, or unusual activity. To resolve this, follow any instructions provided in-app or contact support for assistance."
      }
    ]
  },
  {
    id: 3,
    category: "holding_money",
    slug: "holding-money",
    title: "Holding Money",
    description: "Holding Balances, direct debits, investments and stock options",
    icon: "GiSwipeCard",
    faqs: [
      {
        question: "Can I store money on Cardplot?",
        answer: "Yes, you can securely hold funds in your Cardplot wallet. Your balance is available for transactions such as transfers, payments, and other supported services on the platform."
      },
      {
        question: "Is my money safe on Cardplot?",
        answer: "Absolutely. Cardplot uses advanced security measures and encryption protocols to ensure your funds and personal data are protected at all times."
      },
      {
        question: "Can I hold multiple currencies or wallets?",
        answer: "Yes, Cardplot allows you to create and manage multiple wallets across different supported African countries, making it easy to hold and transact in various currencies."
      },
      {
        question: "Is there a limit to how much I can hold in my wallet?",
        answer: "Holding limits may vary depending on your account type and verification level. Completing your account verification can increase your wallet limits and give you access to more features."
      },
      {
        question: "Can I withdraw my money anytime?",
        answer: "Yes, you can withdraw or transfer your funds at any time, subject to network availability and platform policies. Cardplot is designed to give you flexible access to your money whenever you need it."
      }
    ]
  },
  {
    id: 4,
    category: "currency_conversion",
    slug: "currency-conversion",
    title: "Currency Conversion",
    description: "Rates, Daily updates, arbitrage, etc",
    icon: "LuArrowUpDown",
    faqs: [
      {
        question: "How does currency conversion work on Cardplot?",
        answer: "Cardplot automatically converts your funds based on real-time exchange rates when you send or receive money across different currencies. The converted amount will be displayed before you confirm the transaction."
      },
      {
        question: "Are Cardplot exchange rates updated in real-time?",
        answer: "Yes, Cardplot uses dynamic exchange rates that are updated in real-time to ensure you get accurate and competitive conversion values for your transactions."
      },
      {
        question: "Are there any fees for currency conversion?",
        answer: "Cardplot maintains transparent pricing. Any applicable conversion fees or rates will be clearly shown before you complete your transaction—no hidden charges."
      },
      {
        question: "Can I hold multiple currencies in my Cardplot account?",
        answer: "Yes, Cardplot allows you to create and manage multiple wallets in different African currencies, making it easy to send, receive, and store funds across borders."
      },
      {
        question: "How can I check the current exchange rate before converting?",
        answer: "You can view the current exchange rate directly within the app or web interface while initiating a transaction or when creating or converting between wallets. This ensures full transparency before you proceed."
      }
    ]
  },
  {
    id: 5,
    category: "receiving_money",
    slug: "receiving-money",
    title: "Receiving Money",
    description: "Setting up your account and getting verified",
    icon: "FaArrowLeftLong",
    faqs: [
      {
        question: "How can I receive money on Cardplot?",
        answer: "You can receive money by sharing your Cardplot account details or wallet information with the sender. Once the payment is made, the funds will be credited directly to your account."
      },
      {
        question: "How long does it take to receive money?",
        answer: "Most incoming transactions are processed instantly or within a few minutes. However, timing may vary depending on the sender’s network or payment method."
      },
      {
        question: "Can I receive money from other African countries?",
        answer: "Yes, Cardplot supports cross-border transactions, allowing you to receive money from supported African countries seamlessly."
      },
      {
        question: "Are there any fees for receiving money?",
        answer: "Receiving fees may vary depending on the type of transaction or source. Any applicable charges will always be clearly displayed for transparency."
      },
      {
        question: "What should I do if I don’t receive a payment?",
        answer: "If you haven’t received a payment, confirm the sender has completed the transaction and check that your details were entered correctly. If the issue persists, contact Cardplot support for assistance."
      }
    ]
  },
  {
    id: 6,
    category: "virtual_cards",
    slug: "virtual-cards",
    title: "Virtual Cards",
    description: "Ordering, activating, spending and troubleshooting",
    icon: "IoCard",
    faqs: [
      {
        question: "What is a virtual card on Cardplot?",
        answer: "A virtual card is a digital payment card that allows you to make secure online transactions without needing a physical card. It works just like a regular debit or credit card and can be used for subscriptions, online shopping, and payments worldwide."
      },
      {
        question: "How do I create a virtual card?",
        answer: "To create a virtual card, log in to your Cardplot account, navigate to the Cards section, and click on “Create Virtual Card.” Follow the simple steps, fund your card, and your virtual card will be ready for use instantly."
      },
      {
        question: "Where can I use my Cardplot virtual card?",
        answer: "Your virtual card can be used on any platform that accepts online card payments, including international websites, subscriptions such as streaming services, and e-commerce platforms."
      },
      {
        question: "Is my virtual card secure?",
        answer: "Yes, Cardplot virtual cards are built with advanced security features to protect your transactions. Your card details are encrypted, and you have full control to freeze or manage your card directly from your dashboard."
      },
      {
        question: "Can I fund and withdraw money from my virtual card?",
        answer: "Yes, you can easily fund your virtual card from your Cardplot wallet. Depending on the platform’s features, you may also transfer unused funds back to your wallet when needed."
      }
    ]
  },
  {
    id: 7,
    category: "security_privacy",
    slug: "security-privacy",
    title: "Security & Privacy",
    description: "Account protection, data privacy, and fraud prevention",
    icon: "RiShieldCheckLine",
    faqs: [
      {
        question: "How secure is my personal information on Cardplot?",
        answer: "Your personal information is protected with advanced encryption and security protocols. Cardplot ensures that your data is stored safely and handled in compliance with industry standards to prevent unauthorized access."
      },
      {
        question: "Does Cardplot share my data with third parties?",
        answer: "Cardplot does not sell your personal information. Your data may only be shared with trusted partners when necessary to provide services or comply with legal requirements, and always under strict privacy controls."
      },
      {
        question: "How does Cardplot protect my transactions?",
        answer: "All transactions on Cardplot are secured using encryption and fraud detection systems that monitor activities in real-time. This helps ensure that your funds and transactions remain safe at all times."
      },
      {
        question: "What should I do if I suspect unauthorized activity on my account?",
        answer: "If you notice any suspicious activity, immediately secure your account by changing your password and contacting Cardplot support. Our team will investigate and take the necessary steps to protect your account."
      },
      {
        question: "How can I keep my Cardplot account safe?",
        answer: "To keep your account secure, use a strong password, avoid sharing your login details, and enable any available security features. Always ensure you are accessing Cardplot through official platforms."
      }
    ]
  },
  {
    id: 8,
    category: "technical_support",
    slug: "technical-support",
    title: "Technical Support",
    description: "Troubleshooting app issues, bugs, and contacting support",
    icon: "MdSupportAgent",
    faqs: [
      {
        question: "I can’t log into my account. What should I do?",
        answer: "If you’re unable to log in, ensure you’re entering the correct email and password. You can also try resetting your password using the “Forgot Password” option. If the issue persists, contact support for further assistance."
      },
      {
        question: "I didn’t receive my OTP. What should I do?",
        answer: "Please check your spam or junk folder first. If you still haven’t received your OTP, wait a few moments and request a new one. Ensure your email address is entered correctly."
      },
      {
        question: "The app is not working properly. How can I fix it?",
        answer: "Try closing and reopening the app or updating it to the latest version. You can also clear your app cache or reinstall the app. If the issue continues, reach out to support for help."
      },
      {
        question: "Why is my account temporarily restricted?",
        answer: "Your account may be restricted due to security checks or unusual activity. This is done to protect your funds. Kindly follow any instructions provided or contact support to resolve the issue quickly."
      },
      {
        question: "How do I report a bug or technical issue?",
        answer: "You can report any bugs or technical issues through the Help/Support section within the app or website. Provide as much detail as possible so the issue can be resolved promptly."
      }
    ]
  }
];
